import axios from 'axios';
import { isHiddenProjectId, stripHiddenFromList, HIDDEN_DETAIL_RESPONSE } from '../config/hiddenProjects';

// Use local proxy to avoid CORS issues
const API_BASE_URL = '/api/proxy';

// API token from environment variable for security
const API_TOKEN = import.meta.env.VITE_API_TOKEN;

if (!API_TOKEN) {
    console.warn('[API] Warning: VITE_API_TOKEN is not set. API requests will fail.');
}

const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Authorization': `Bearer ${API_TOKEN}`,
        'Content-Type': 'application/json'
    }
});

export const fetchProjects = async (page = 1, perPage = 10) => {
    try {
        const response = await apiClient.post('/websites_project_list', {
            per_page: perPage,
            page: page
        });
        // The production proxy already strips hidden projects; this covers
        // the dev server, whose proxy goes straight to REM.
        return stripHiddenFromList(response.data);
    } catch (error) {
        console.error("Error fetching projects:", error);
        throw error;
    }
};

export const fetchProjectDetails = async (projectId) => {
    if (isHiddenProjectId(projectId)) return HIDDEN_DETAIL_RESPONSE;
    try {
        const response = await apiClient.post('/websites_project_detail', {
            fk_project_id: projectId
        });
        return response.data;
    } catch (error) {
        console.error("Error fetching project details:", error);
        throw error;
    }
};

export const searchProjects = async (filters = {}) => {
    try {
        const params = {
            per_page: filters.per_page || 20,
            page: filters.page || 1
        };

        // Strict mapping based on REM Offplan API PDF
        if (filters.title) params.title = filters.title; // Project Name
        if (filters.district_name) params.district_name = filters.district_name; // Location/District
        if (filters.city_name) params.city_name = filters.city_name;

        // Price Range
        if (filters.price_min) params.price_min = filters.price_min;
        if (filters.price_max) params.price_max = filters.price_max;

        // Area Range
        if (filters.area_min) params.area_min = filters.area_min;
        if (filters.area_max) params.area_max = filters.area_max;

        // Properties
        if (filters.beds) params.beds = filters.beds;
        if (filters.baths) params.baths = filters.baths;
        if (filters.property_type) params.property_type = filters.property_type;

        // Project status. Must be forwarded or the "Launching Soon" category
        // silently degrades into the full unfiltered list.
        if (filters.status) params.status = filters.status;

        // Developer
        if (filters.developer_name) params.developer_name = filters.developer_name;

        // Completion
        if (filters.completion_date) params.completion_date = filters.completion_date;

        const response = await apiClient.post('/websites_project_list', params);
        return stripHiddenFromList(response.data);
    } catch (error) {
        console.error("Error searching projects:", error);
        throw error;
    }
};

// Dubai areas for location dropdown
// District names EXACTLY as the REM API stores them (district_name is matched
// server-side as a substring, so any deviation — e.g. "Jumeirah Village Circle (JVC)"
// instead of "JVC (Jumeirah Village Circle)" — returns zero results).
// Generated from a full scan of the project list; covers all UAE emirates, not just Dubai.
export const DUBAI_AREAS = [
    "Ajman Industrial 1",
    "Al Ain",
    "Al Amerah",
    "Al Arqoub",
    "Al Aweer",
    "Al Bahyah",
    "Al Barari",
    "Al Barsha",
    "Al Barsha South 3",
    "Al Bedea",
    "Al Fiqait",
    "Al Furjan",
    "Al Hamra Free Zone",
    "Al Hamra Village",
    "Al Hamriya",
    "Al Hebiah 5",
    "Al Heliow",
    "Al Hudayriat Island",
    "Al Jaddaf",
    "Al Jaddaf Waterfront",
    "Al Jazeera Al Hamra",
    "Al Jazeera Al Hamra Industrial",
    "Al Jurf Gardens",
    "Al Kasir",
    "Al Khaledia Suburb",
    "Al Khan 2",
    "Al Kifaf",
    "Al Layyan",
    "Al Mamzar-1",
    "Al Marjan Island",
    "Al Maryah Island",
    "AL MENHAZ",
    "Al Nakhil 2",
    "Al Nuaimia 1,2",
    "Al Quoz 2",
    "Al Raha Beach",
    "Al Rahah",
    "Al Rashidiya 1",
    "Al Rawdah",
    "Al Reem Island",
    "Al Rifa'ah",
    "Al Rowaiyah First",
    "Al Rowdat Suburb",
    "Al Rumailah 2",
    "Al Rumailah 3",
    "Al Saadiyat island",
    "al satwa",
    "Al Shamkhah",
    "Al Sufouh",
    "Al Tay East",
    "Al Wadi desert",
    "Al Wasl",
    "Al Yalayis 1",
    "Al Yasmeen",
    "Al Yufrah 1",
    "Al Zahya",
    "AL Zawarah",
    "Al Zorah City",
    "Aljada",
    "Arabian Ranches 3",
    "Arjan",
    "Azizi Riviera at Meydan One",
    "Blue Waters Island",
    "Bu kadra",
    "Bukadra",
    "Business Bay",
    "Cherrywoods",
    "City Of Arabia",
    "City Walk",
    "Damac Hills",
    "Damac Hills 2",
    "Damac Islands",
    "Damac Islands 2",
    "Damac Lagoons",
    "Damac Riverside",
    "Damac Suncity",
    "DIFC (Dubai International Financial Center)",
    "Discovery Gardens",
    "Downtown Dubai",
    "Downtown Jebel Ali",
    "Downtown Umm Al Quwain",
    "Dubai Creek",
    "Dubai Creek Harbour",
    "Dubai Design District",
    "Dubai Expo City",
    "Dubai Harbour",
    "Dubai Healthcare City",
    "Dubai Hills",
    "Dubai Industrial City",
    "Dubai International City",
    "Dubai Internet City",
    "Dubai Investments Park",
    "Dubai Islands",
    "Dubai Land",
    "Dubai Marina",
    "Dubai Media City",
    "Dubai Motor City",
    "Dubai Production City",
    "Dubai Science Park",
    "Dubai Silicon Oasis",
    "Dubai South",
    "Dubai Sports City",
    "Dubai Studio City",
    "Dubailand Residence Complex",
    "Elite Island",
    "Emaar Beach Front",
    "Emaar South",
    "Fahid Island",
    "Falconcity of Wonders",
    "Ghadeer Al Tayr",
    "Ghantout",
    "Grand Polo Club and Resort",
    "Green Gate at Dubai Creek Harbour",
    "Hessian Second",
    "Jabal Ali First",
    "Jabal Ali Industrial 2",
    "Jabal Ali Second",
    "Jebel Ali First",
    "Jebel Ali Freezone Extension",
    "Jebel Ali Village",
    "JLT (Jumeirah Lake Towers)",
    "Jumeirah Beach Residence (JBR)",
    "Jumeirah First",
    "Jumeirah Garden City",
    "Jumeirah Golf Estates",
    "Jumeirah Islands",
    "Jumeirah Park",
    "Jumeirah Second",
    "JVC (Jumeirah Village Circle)",
    "JVT (Jumeirah Village Triangle)",
    "Khalifa City",
    "Khalifa Industrial District",
    "Liwan",
    "Madinat Al Mataar",
    "Majan",
    "Maritime City",
    "Maryam Island",
    "Masdar City",
    "MBR District 1",
    "MBR District 11 (Meydan South)",
    "Meydan",
    "Meydan (Nad Al Sheba  1)",
    "Meydan one",
    "Mina Al Arab",
    "Mina Rashid",
    "Mirdif",
    "MJL (Madinat Jumeirah Living)",
    "Mudon",
    "Muwaileh Commercial",
    "Nad Al Sheba Gardens",
    "Nadd Al Нamar",
    "Palm Deira",
    "Palm Jebel Ali",
    "Palm Jumeirah",
    "Pearl Jumeirah",
    "Peninsula Island",
    "Port De La Mer",
    "Rabdan",
    "Ragayeb",
    "Rak Central",
    "Ramhan Island",
    "Ras Al Khor",
    "Reem",
    "Reem Island",
    "Remraam",
    "Safa Park",
    "Saih Shuaib",
    "Saih Shuaib 2",
    "Sanaya 2",
    "Shakhbout city",
    "Sharjah Garden City",
    "Siniya Island",
    "Sobha Central",
    "Sobha Hartland",
    "Sobha Hartland 2",
    "Sobha Sanctuary",
    "The Heights",
    "The Oasis",
    "The Valley",
    "The Villa",
    "Tilal",
    "Tilal Al Ghaf",
    "Town Square",
    "Trade Center",
    "Umm Al Daman",
    "Umm Nahad 3/Madinat Hind 3",
    "UMM SUQEIM",
    "Wadi Al Safa 2",
    "Wadi Al Safa 3",
    "Wadi al safa 5",
    "Wadi Al Safa 7",
    "warsan 4",
    "Wasl Gate",
    "World of Islands",
    "Yas Island",
    "Zabeel 1&2",
    "Zayed City"
];

// Property types EXACTLY as the REM API spells them. Anything outside this
// vocabulary (e.g. "Apartment" singular, "Office", "Retail") returns zero rows.
// `property_type` also accepts an array, which the API treats as an OR.
export const PROPERTY_TYPES = [
    { value: 'Apartments', labelKey: 'hero.search.apartment' },
    { value: 'Villa', labelKey: 'hero.search.villa' },
    { value: 'Townhouse', labelKey: 'hero.search.townhouse' },
    { value: 'Penthouse', labelKey: 'hero.search.penthouse' },
    { value: 'Duplex', labelKey: 'hero.search.duplex' },
    { value: 'Mansion', labelKey: 'hero.search.mansion' },
    { value: 'Hotel apartments', labelKey: 'hero.search.hotel_apartment' },
    { value: 'Commerce', labelKey: 'hero.search.commercial' },
    { value: 'Plot', labelKey: 'hero.search.plot' }
];

// Category mappings for navigation buttons
// Note: 'trusted' category will be added later for client-uploaded projects via Re-Sale
export const CATEGORY_FILTERS = {
    // Every project this API serves is off-plan, and `is_offplan` is ignored
    // server-side, so this is deliberately the unfiltered list.
    'off-plan': {},
    // تجاري — the API's commercial bucket is the single type "Commerce".
    // "Office"/"Shop"/"Retail" are not in its vocabulary and match nothing.
    'commercial': { property_type: 'Commerce' },
    // قريباً — projects announced but not yet on sale.
    'launching-soon': { status: 'Announced' }
};

// Fetch projects by category for navigation buttons
export const fetchProjectsByCategory = async (category, page = 1, perPage = 12) => {
    const categoryFilters = CATEGORY_FILTERS[category] || {};
    return searchProjects({ ...categoryFilters, page, per_page: perPage });
};

export default apiClient;
